import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ChannelWithMembership, Team } from '@avslack/shared';
import { listTeams } from '../lib/api';

/**
 * Cegvalto.
 *
 * ─── Miert nem keverednek a cegek egy listaban? ─────────────────────────────
 *
 * Mert egy masik ceg csatornaja nem "meg egy csatorna": mas emberek, mas
 * munka, mas bizalmi kor. Egy listaban ulve minden nap el kellene olvasni a
 * nevet ahhoz, hogy tudjuk, kinek irunk - es egyszer valaki nem olvasna el.
 *
 * A valto ezt szerkezettel oldja meg: egyszerre EGY ceg latszik. Ami nem
 * valtozik: az ertesites cegeken at is megerkezik - a valto csak azt donti el,
 * mit LATUNK, nem azt, mirol tudunk.
 */

const TEAM_KEY = 'avslack.team';

export function useTeams() {
  return useQuery({
    queryKey: ['teams'],
    queryFn: listTeams,
    staleTime: 5 * 60_000,
  });
}

interface ActiveTeam {
  activeTeamId: string;
  setActiveTeam: (teamId: string) => void;
}

const ActiveTeamContext = createContext<ActiveTeam | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(TEAM_KEY);
  } catch {
    return null;
  }
}

/**
 * A nyitott ceg - ujratoltest is tullep.
 *
 * A tarolt ertek csak akkor ervenyes, ha a felhasznalo MA IS mozoghat abban a
 * cegben: ha kikerult a kozos csatornabol, a valto nem ragadhat egy olyan
 * nezetben, ahol mar nincs semmi. Ilyenkor visszaesunk a sajat csapatra.
 */
export function ActiveTeamProvider({
  homeTeamId,
  teams,
  children,
}: {
  homeTeamId: string;
  teams: Team[];
  children: React.ReactNode;
}) {
  const [chosen, setChosen] = useState<string | null>(readStored);

  const activeTeamId = useMemo(() => {
    const ervenyes = chosen && teams.some((team) => team.id === chosen);
    return ervenyes ? chosen : homeTeamId;
  }, [chosen, teams, homeTeamId]);

  const setActiveTeam = useCallback((teamId: string) => {
    setChosen(teamId);
    try {
      localStorage.setItem(TEAM_KEY, teamId);
    } catch {
      // A tarolas hianya nem torheti el a valtast - csak nem jegyezzuk meg.
    }
  }, []);

  const value = useMemo(() => ({ activeTeamId, setActiveTeam }), [activeTeamId, setActiveTeam]);

  return <ActiveTeamContext.Provider value={value}>{children}</ActiveTeamContext.Provider>;
}

export function useActiveTeam(): ActiveTeam {
  const value = useContext(ActiveTeamContext);
  if (!value) throw new Error('A useActiveTeam csak ActiveTeamProvider alatt használható.');
  return value;
}

/**
 * A csatorna EBBEN a ceg-nezetben latszik-e.
 *
 * A rendes csatornaknak sajat csapatuk van. A DM-eknek nincs - azoknal a
 * szerver mondja meg, melyik ceg nezetebe tartoznak (`dmTeamIds`), mert csak
 * o latja a tobbi csatorna tagsagat is.
 */
export function belongsToTeam(channel: ChannelWithMembership, teamId: string): boolean {
  if (channel.kind === 'dm' || channel.kind === 'group_dm') {
    return (channel.dmTeamIds ?? []).includes(teamId);
  }
  return channel.teamId === teamId;
}
